import React, { useEffect, useState, useCallback } from "react";
import { capitalize, debounce } from "lodash";
import { Box, Button, Card, CardContent, CardHeader, IconButton, Input, InputAdornment, Tooltip, Dialog, DialogActions, DialogTitle, FormGroup, FormControlLabel, Switch } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { modelServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { EditOutlined, Money, PriceChange } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";

export default function ModelManagementList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);


  const handleApprove = async (id, action) => {

    try {
      const payload = {
        id: id,
        isVerified: action,
      }

      const res = await modelServices.modelverify(payload);
      if (res.status === 200) {
        console.log(res, "res")
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await modelServices.getmodel(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this Model?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await modelServices.deleteModel({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    console.log(currentStatus);
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await modelServices.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "User Name",
      sortable: true,
      selector: (row) => row.username,
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email,
    },
    {
      name: "Phone",
      sortable: true,
      selector: (row) => row.phoneNumber,
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"
                    } this user?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },

    {
      name: "Accept/Reject",
      cell: (row) =>
        row.isApproved === "Pending" ? (
          <>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleApprove(row._id, "Accepted")}
            >
              Accept
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => handleApprove(row._id, "Rejected")}
              sx={{ ml: 1 }}
            >
              Reject
            </Button>
          </>
        ) :
          <Button
            variant="contained"
            color="success"
            size="small"
            disabled
          >
            {row.isApproved}
          </Button>
      ,
    },

    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
          {row.profileCompleted ?
            <Tooltip title="Comission" placement="top">
              <IconButton
                color="primary"
                onClick={() => {
                  navigate(`/performer-management/model-commission/${row?._id}`);
                }}
              >

                <PriceChange />
              </IconButton>
            </Tooltip>
            : null}
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/performer-management/view/${row?._id}`);
              }}             
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/performer-management/edit/${row?._id}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title="Performer Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />

        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  setSearchItem("");
                  fetchUsers("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        <Button
          size="large"
          type="submit"
          variant="contained"
          sx={{ ml: 3, mr: 3 }}
          onClick={() => navigate("/performer-management/add")}
        >
          Add
        </Button>
      </Box>
      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
      </CardContent>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        className="popupbox-statis"
      >
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}